import { useEffect, useState } from 'react';
import AmpDialog from '@apps/components/Amp/AmpDialog';
import Gallery from '@apps/components/Gallery';
import StyledImage from '@apps/components/Image';
import { SocialIcons } from '@apps/config/icons.config';
import AdsContainer from '@apps/containers/AdsContainer';
import { dateIsoFormat } from '@apps/utils/date-conversion';
import OpenIcon from '@stack/assets/icons/open.svg';
import TagIcon from '@stack/assets/icons/tag.svg';
import avatarPlaceholder from '@stack/assets/images/avatar_placeholder.jpeg';
import classNames from 'classnames';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

import { SocialIconButton } from '../../components/SocialIconButton/index';

import NextPrevNews from './NextPrevNews';
import {
  AuthorWrapper,
  BadgeLabel,
  ContentHeaderWrapper,
  FloatingBox,
  FloatingBoxWrapper,
  ListWrapper,
  NewsDetailSectionWrapper,
  NewsDetailTitle,
  NewsDetailTitleWrapper,
  PostContentWrapper,
  ShareArticleLabel,
  ShareWrapper,
  TagsContainer,
  TagsWrapper,
} from './styles';

export type NewsDetailSectionProps = {
  title: string;
  category: string;
  authorName: string;
  authorUsername: string;
  authorAvatar: string;
  authorDescription: string;
  createdDate: string;
  coverImage: string;
  content: string;
  tags: {
    label: string;
    slug: string;
  }[];
  shareUrl: string;
  nextNews: {
    title: string;
    slug: string;
    avatar: string;
    id: number;
    createdAt: string;
  };
  prevNews: {
    title: string;
    slug: string;
    avatar: string;
    id: number;
    createdAt: string;
  };
  galleryImages?: string[];
  webView?: boolean;
};

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const NewsDetailSection: React.FC<NewsDetailSectionProps> = (props) => {
  const {
    title,
    category,
    authorName,
    authorUsername,
    authorAvatar,
    authorDescription,
    createdDate,
    content,
    tags,
    shareUrl,
    nextNews,
    prevNews,
    galleryImages,
    webView = false,
  } = props;

  // ======================= HOOKS
  const { t } = useTranslation('common');
  const [toast, setToast] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string>('Link Copied');
  const [floatingState, setFloatingState] = useState<boolean>(false);
  const createDateIsoFormart = dateIsoFormat(createdDate) as string;

  // ======================= STATES
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);

  useEffect(() => {
    if (showShareDialog) {
      document.body.style.overflow = 'hidden';
    } else document.body.style.overflow = 'scroll';
    return () => {};
  }, [showShareDialog]);

  // ======================= RENDER FUNCTIONS
  const openSnackBar = (message = 'Something went wrong...') => {
    setToast(true);
    setToastMsg(message);
    setTimeout(() => {
      setToast(false);
    }, 3000);
  };

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop } = document.documentElement;
      if (scrollTop > 295) {
        setFloatingState(true);
      } else {
        setFloatingState(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const renderSocialIconButtons = (socialOnly?: boolean) => {
    return (
      <div
        style={{
          display: 'flex',
          gap: 16,
          flexDirection: 'row',
        }}
      >
        <SocialIconButton
          isSvg
          href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
        >
          {SocialIcons['facebook']}
        </SocialIconButton>
        {/* <SocialIconButton
          isSvg
          href={`https://www.instagram.com/?url=${shareUrl}`}
        >
          {SocialIcons['instagram']}
        </SocialIconButton> */}

        <SocialIconButton
          isSvg
          href={`https://twitter.com/intent/tweet?text=${title}&url=${shareUrl}`}
        >
          {SocialIcons['twitter']}
        </SocialIconButton>

        <SocialIconButton
          isSvg
          href={`https://www.tumblr.com/widgets/share/tool?canonicalUrl=${shareUrl}`}
        >
          {SocialIcons['tumblr']}
        </SocialIconButton>

        {!socialOnly && (
          <SocialIconButton
            isSvg
            ampType="copy"
            href={shareUrl}
            onClick={() => {
              openSnackBar(t('linkCopy'));
              navigator.clipboard.writeText(shareUrl);
            }}
          >
            {SocialIcons['copy']}
          </SocialIconButton>
        )}

        {!socialOnly && (
          <SocialIconButton
            isSvg
            ampType="system"
            href={shareUrl}
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: title,
                  text: title,
                  url: shareUrl,
                });
              } else {
                setShowShareDialog(true);
              }
            }}
          >
            {SocialIcons['share']}
          </SocialIconButton>
        )}
      </div>
    );
  };
  const renderShareVia = ({ isSticky = false }) => (
    <div
      style={{
        display: 'flex',
        gap: isSticky ? 20 : 16,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: isSticky ? 'space-between' : 'flex-start',
      }}
    >
      <span
        style={{
          fontSize: 14,
          fontWeight: 400,
        }}
      >
        {t('newsDetail.shareVia')}
      </span>
      {renderSocialIconButtons()}
    </div>
  );

  const renderNewsContent = () => {
    return (
      <div
        className="post-description"
        dangerouslySetInnerHTML={{ __html: content }}
      ></div>
    );
  };

  const renderTag = (label: string, href: string) => {
    if (webView)
      return (
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            backgroundColor: '#F3F4F6',
            padding: '4px 8px',
            borderRadius: 4,
          }}
        >
          <span>{label}</span>
        </div>
      );
    return (
      <Link href={`/tag/${href}`} key={`tag-${label}`}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            backgroundColor: '#F3F4F6',
            padding: '4px 8px',
            borderRadius: 4,
          }}
        >
          <span>{label}</span>
        </div>
      </Link>
    );
  };

  const renderTagGroup = () => {
    if (!tags || tags.length === 0) return null;
    return (
      <TagsContainer>
        <TagsWrapper>
          <div
            style={{
              display: 'flex',
              gap: 4,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
              }}
            >
              <TagIcon />
            </div>
            <p
              style={{
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {`${t('newsDetail.tagged')}:`}
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 9,
              flexWrap: 'wrap',
            }}
          >
            {tags.map((tag) => {
              if (!tag.label) return;
              return renderTag(tag.label, tag.slug);
            })}
          </div>
        </TagsWrapper>
      </TagsContainer>
    );
  };

  const renderFloatingSocialLinksBox = () => {
    return (
      <FloatingBox>
        <div
          style={{
            padding: '12px 20px',
            width: '100%',
          }}
        >
          {renderShareVia({ isSticky: true })}
        </div>
      </FloatingBox>
    );
  };

  // ======================= VIEWS
  return (
    <NewsDetailSectionWrapper>
      <AmpDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #A8B5C9',
              padding: '8px 16px',
            }}
          >
            <p
              style={{
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              {t('newsDetail.shareThisArticle')}
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <button
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => setShowShareDialog(false)}
              >
                <span
                  className="material-symbols-outlined text-xl"
                  style={{
                    color: '#A8B5C9',
                  }}
                >
                  close
                </span>
              </button>
            </div>
          </div>
          <div
            style={{
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 15,
              // justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {renderSocialIconButtons(true)}
            <div
              style={{
                width: '100%',
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  marginBottom: 8,
                }}
              >
                or copy this link
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <input
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '0px solid #A8B5C9',
                    borderRadius: 4,
                    backgroundColor: '#F3F4F6',
                    fontSize: 14,
                    color: '#1E242F',
                  }}
                  value={shareUrl}
                  readOnly
                />
                <button
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#31ACBF',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                    openSnackBar(t('linkCopy'));
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      </AmpDialog>
      {/* title with author and share */}
      <ContentHeaderWrapper>
        <div
          className={classNames('snackbar text-sm', {
            show: toast,
          })}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
            }}
          >
            {toastMsg}
            <span
              className="material-symbols-outlined text-xl"
              style={{
                color: '#31acbf',
              }}
            >
              check_circle
            </span>
          </div>
        </div>
        <NewsDetailTitleWrapper>
          <div>
            <BadgeLabel>{category}</BadgeLabel>
          </div>
          <NewsDetailTitle>{title}</NewsDetailTitle>
        </NewsDetailTitleWrapper>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            marginBottom: -18,
          }}
        >
          {/* author */}
          <AuthorWrapper>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                overflow: 'hidden',
              }}
            >
              <StyledImage
                src={authorAvatar || avatarPlaceholder.src}
                alt=""
                width={60}
                height={60}
              />
            </div>
            <div
              style={{
                fontSize: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {webView ? (
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: '12.65px',
                  }}
                >
                  {authorName}
                </p>
              ) : (
                <Link
                  className="url-ani-2"
                  href={`/author/${authorUsername?.toLowerCase()}`}
                >
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: '12.65px',
                    }}
                  >
                    {authorName}
                  </p>
                </Link>
              )}
              <p
                style={{
                  fontWeight: 400,
                }}
              >
                {/* {`${t('newsDetail.writtenBy')} ${createdDate}`} */}
                <time dateTime={createDateIsoFormart}>{createdDate}</time>
              </p>
            </div>
          </AuthorWrapper>
          {renderShareVia({ isSticky: false })}
        </div>
      </ContentHeaderWrapper>
      {/* cover image */}
      {/* <StyledImage
        src={coverImage ?? DetailImage.src}
        alt=""
        width={850}
        height={557}
      /> */}
      {/* content */}
      <PostContentWrapper>
        <ListWrapper className="list">
          {renderNewsContent()}
          <AdsContainer
            adSlot={'5942712071'}
            style={{
              display: 'block',
              width: '100%',
              minHeight: '270px',
            }}
            containerStyle={{
              paddingTop: 0,
            }}
            localPlacement={'lg_details'}
          />
          {galleryImages && galleryImages.length > 0 && (
            <Gallery images={galleryImages} />
          )}
          {renderTagGroup()}
        </ListWrapper>
      </PostContentWrapper>
      {/* share this article with author and next/prev */}
      <div>
        {/* share */}
        <ShareWrapper>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              borderTop: '1px dotted #A8B5C9',
              paddingTop: 16,
              paddingBottom: 16,
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                }}
              >
                <OpenIcon />
              </div>
              <ShareArticleLabel>
                {t('newsDetail.shareThisArticle')}
              </ShareArticleLabel>
            </div>
            {renderSocialIconButtons()}
          </div>
        </ShareWrapper>
        {/* author */}
        <div
          style={{
            borderTop: '1px dotted #A8B5C9',
            paddingTop: 16,
            paddingBottom: 16,
            display: 'flex',
            gap: 20,
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                overflow: 'hidden',
              }}
            >
              <StyledImage
                src={authorAvatar || avatarPlaceholder.src}
                alt=""
                width={50}
                height={50}
              />
            </div>
            <div
              style={{
                fontSize: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <p
                style={{
                  fontWeight: 400,
                }}
              >
                {t('newsDetail.writtenBy')}
              </p>
              {webView ? (
                <p
                  style={{
                    fontWeight: 700,
                    fontSize: '12.65px',
                  }}
                >
                  {authorName}
                </p>
              ) : (
                <Link
                  className="url-ani-2"
                  href={`/author/${authorUsername?.toLowerCase()}`}
                >
                  <p
                    style={{
                      fontWeight: 700,
                      fontSize: '12.65px',
                    }}
                  >
                    {authorName}
                  </p>
                </Link>
              )}
            </div>
          </div>
          {
            // author description
            authorDescription && (
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: '22px',
                }}
              >
                {authorDescription}
              </p>
            )
          }
        </div>
        {/* next/prev */}
        {nextNews?.title && prevNews?.title && (
          <div
            style={{
              borderTop: '1px dotted #A8B5C9',
              borderBottom: '1px dotted #A8B5C9',
              paddingTop: 16,
              paddingBottom: 16,
              // display: 'flex',
              // gap: 20,
              // flexDirection: 'column',
            }}
          >
            <NextPrevNews
              nextNews={nextNews}
              prevNews={prevNews}
              webView={webView}
            />
          </div>
        )}
      </div>
      <FloatingBoxWrapper $show={floatingState}>
        {renderFloatingSocialLinksBox()}
      </FloatingBoxWrapper>
    </NewsDetailSectionWrapper>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default NewsDetailSection;
